'use client';
import { useState, useEffect, useRef } from 'react';
import { classifyIntake, DEFAULT_SMART_INTAKE_RULES } from '@/lib/smartIntakeEngine';
import { setSmartIntakeDemo, setKnownCity, notifySmartIntake } from '@/lib/smartIntakeStore';
import type { HomeSample } from '@/lib/homeSamples';
import { findCity, citySlug } from '@/lib/israelCities'; // citySlug = the ONE way to build a city URL (סח׳נין 404, bughunter)

export type SmartIntakeContext = {
  categorySlug: string | null;
  categoryName: string | null;
  city: string | null;
  cleanNeed: string | null;
  priceHint: string | null;
  timingHint: string | null;
  sessionId?: string | null;
  customNeed?: string | null; // user's own free-text request (overrides the templated message)
  cityLat?: number | null;    // resolved city coordinates (for radius filtering)
  cityLng?: number | null;
  cityChosenAt?: number | null; // 2026-09-28: stamped ONLY when the visitor actively picked/typed a city (never by a page load)
  // Wall-clock stamp (2026-08-19) of the last write that resolved `city` to a
  // DEFINITIVE value — a live answer, or a /{category}/{city} page's own known
  // presetCity. Lets readers (CategoryExpertsList) tell a genuinely fresh city pick
  // apart from a stale value left over from a different page's session — see the
  // cityAnsweredAt guard there and in QuestionnaireWizard's savePartialCtx.
  cityAnsweredAt?: number | null;
  // Raw questionnaire answers (2026-07-29) — outputKey → chosen label/free-text, in
  // answer order. Lets the server compose the per-article natural-language message
  // (he_article_hooks.message_template, lib/waLinks.ts) from the actual answers instead
  // of the generic comma-joined cleanNeed.
  answers?: { key: string; label: string }[];
};
export const SMART_INTAKE_KEY = 'smartIntakeCtx';

type CategoryHint = { slug: string; name_he: string };
type Fields = { cat: string | null; city: string | null; need: string | null; timing: string | null };

// Full singular profession map — plural DB names → display singular
export const SINGULAR: Record<string, string> = {
  'חשמלאים': 'חשמלאי',
  'אינסטלטורים': 'אינסטלטור',
  'שרברבים': 'שרברב',
  'עורכי דין': 'עורך דין',
  'רואי חשבון': 'רואה חשבון',
  'מתקיני דלתות': 'מתקין דלתות',
  'דלתות פנים': 'מתקין דלתות',
  'נגרים': 'נגר',
  'צבעים': 'צבע',
  'טכנאי מזגנים': 'טכנאי מזגן',
  'גננים': 'גנן',
  'קבלני שיפוצים': 'קבלן שיפוצים',
  'שיפוצניקים': 'שיפוצניק',
  'קבלני גבס': 'קבלן גבס',
  'קבלני בניה קלה': 'קבלן בנייה',
  'קבלני שלד': 'קבלן שלד',
  'קבלני איטום': 'קבלן איטום',
  'קבלני אלומיניום': 'קבלן אלומיניום',
  'קבלני שיש': 'קבלן שיש',
  'קבלני ממדים': 'קבלן ממד',
  'קבלני מצבות': 'קבלן מצבות',
  'מפקחי בנייה': 'מפקח בנייה',
  'קונסטרוקטורים': 'קונסטרוקטור',
  'אדריכלים': 'אדריכל',
  'הנדימנים': 'הנדימן',
  'מסגרים': 'מסגר',
  'גגנים': 'גגן',
  'מנסרי וקודחי בטון': 'מנסר בטון',
  'אנשי עבודות בגובה': 'עבודות בגובה',
  'מפני דירות': 'מפנה דירות',
  'חברות שיקום מבנים': 'שיקום מבנים',
  'חברות ניהול ואחזקת מבנים': 'ניהול מבנים',
  'מצפי דלתות': 'מצפה דלתות',
  'זגגים': 'זגג',
  'אנשי תריסים': 'מתקין תריסים',
  'מתקיני סוככים': 'מתקין סוככים',
  'מתקיני פרגולות אלומיניום': 'מתקין פרגולות',
  'מתקיני פרקטים': 'מתקין פרקטים',
  'מדביקי טפטים': 'מדביק טפטים',
  'מעצבות פנים': 'מעצבת פנים',
  'מסדרות בתים': 'מסדרת בית',
  'מאתרי נזילות': 'מאתר נזילות',
  'מתקיני מקלחונים': 'מתקין מקלחונים',
  'חברות ביובית': 'ביובית',
  'אמבטיונרים': 'אמבטיונר',
  'אנשי ייבוש תת רצפתי': 'ייבוש תת רצפתי',
  'טכנאי גז': 'טכנאי גז',
  'מתקיני מערכות חימום': 'מתקין חימום',
  'טכנאי דודי שמש': 'טכנאי דוד שמש',
  'טכנאי מערכות סולאריות': 'טכנאי סולארי',
  'טכנאי אזעקות ומצלמות': 'טכנאי אזעקות',
  'טכנאי אינטרקום וקודנים': 'טכנאי אינטרקום',
  'טכנאי שערים חשמליים': 'טכנאי שערים',
  'בודקי חשמל': 'בודק חשמל',
  'מתקיני מערכות כיבוי אש': 'מתקין כיבוי אש',
  'בודקי קרינה': 'בודק קרינה',
  'טכנאי מקררים': 'טכנאי מקררים',
  'טכנאי מוצרי חשמל': 'טכנאי חשמל',
  'טכנאי טלויזיה': 'טכנאי טלויזיה',
  'טכנאי מחשבים': 'טכנאי מחשבים',
  'טכנאי רשתות': 'טכנאי רשתות',
  'מעבדות סלולר': 'מעבדת סלולר',
  'חברות דיגיטציה': 'חברת דיגיטציה',
  'חשמלאי רכב': 'חשמלאי רכב',
  'זגגי רכב': 'זגג רכב',
  'מוסכים': 'מוסך',
  'מוסכים ניידים': 'מוסך נייד',
  'מוסכי קטנועים': 'מוסך קטנועים',
  'פחחי רכב': 'פחח רכב',
  'מנעולני רכב': 'מנעולן רכב',
  'מתקיני מערכות לרכב': 'מתקין רכב',
  'דיטיילינג וציפויים לרכב': 'דיטיילינג רכב',
  'שמאי רכב': 'שמאי רכב',
  'גוזמי עצים': 'גוזם עצים',
  'מקימי גינות': 'מקים גינות',
  'נגרי פרגולות ודקים': 'נגר פרגולות',
  'חברות ניקיון ופוליש': 'חברת ניקיון',
  'חברות ניקוי ספות': 'ניקוי ספות',
  'מדבירים': 'מדביר',
  'מרחיקי יונים': 'מרחיק יונים',
  'מובילים': 'מוביל',
  'מובילים להובלות קטנות': 'מוביל קטן',
  'נגרים למטבחים': 'נגר מטבחים',
  'חברות מטבחים': 'חברת מטבחים',
  'מנעולנים': 'מנעולן',
  'עורכי דין עבודה': 'עורך דין עבודה',
  'עורכי דין חובות': 'עורך דין חובות',
  'עורכי דין מסחרי': 'עורך דין מסחרי',
  'עורכי דין מקרקעין': 'עורך דין מקרקעין',
  'עורכי דין משפחה': 'עורך דין משפחה',
  'עורכי דין נזיקין': 'עורך דין נזיקין',
  'עורכי דין תעבורה': 'עורך דין תעבורה',
  'נוטריונים': 'נוטריון',
  'יועצי מס': 'יועץ מס',
  'יועצי משכנתאות': 'יועץ משכנתאות',
  'מנהלי חשבונות': 'מנהל חשבונות',
  'סוכני ביטוח': 'סוכן ביטוח',
  'שמאי מקרקעין': 'שמאי מקרקעין',
  'שמאי רכוש': 'שמאי רכוש',
  'מתווכים': 'מתווך',
  'מודדים מוסמכים': 'מודד מוסמך',
  'רופאי שיניים': 'רופא שיניים',
  'אורתודנטים': 'אורתודנט',
  'אופטומטריסטים': 'אופטומטריסט',
  'פיזיותרפיסטים': 'פיזיותרפיסט',
  'נטורופתים': 'נטורופת',
  'רפלקסולוגים': 'רפלקסולוג',
  'מעסים מקצועיים': 'מעסה',
  'מטפלים לדיקור סיני': 'דיקור סיני',
  'מטפלות אקוויליבריו': 'מטפלת אקוויליבריו',
  'דיאטניות': 'דיאטנית',
  'מוהלים': 'מוהל',
  'מאמני כושר': 'מאמן כושר',
  'יועצות זוגיות': 'יועצת זוגית',
  'יועצות שינה': 'יועצת שינה',
  'מדריכות הורים': 'מדריכת הורים',
  'מדריכות הנקה': 'מדריכת הנקה',
  'מורים לנהיגה': 'מורה נהיגה',
  'מאפרות ומעצבות שיער': 'מאפרת / מעצבת שיער',
  'קוסמטיקאיות': 'קוסמטיקאית',
  'רפדים': 'רפד',
  'מנהלות ומפיקות אירועים': 'מפיקת אירועים',
  'מעצבות אירועים': 'מעצבת אירועים',
  'ספקי אטרקציות לאירועים': 'ספק אטרקציות',
  'חברות להשכרת ציוד לאירועים': 'השכרת ציוד',
  'חברות קייטרינג': 'קייטרינג',
  'מפעילי ימי הולדת': 'מפעיל ימי הולדת',
  'תקליטנים ונגנים': 'תקליטן / נגן',
  'צלמים': 'צלם',
  'מעצבים גרפיים': 'מעצב גרפי',
  'ספקי מגשי אירוח': 'ספק מגשי אירוח',
  'שפים פרטיים': 'שף פרטי',
  'מכוני גירים': 'מכון גירים',
  'וטרינרים': 'וטרינר',
  'מאלפי כלבים': 'מאלף כלבים',
  'ספרי כלבים': 'ספר כלבים',
  'פנסיוני כלבים': 'פנסיון כלבים',
  'חברות בדק בית': 'בדק בית',
  'חברות הסעות': 'חברת הסעות',
  'בתי דפוס': 'בית דפוס',
  'דולות': 'דולה',
  'מפעילי בריכות': 'מפעיל בריכות',
  'מקימי בריכות שחייה': 'מקים בריכות',
  'בוני אקווריומים ובריכות נוי': 'בונה אקווריום',
  'חברות אמבולנס פרטי': 'אמבולנס פרטי',
  'מפעילי מנוף': 'מפעיל מנוף',
  'מכווני פסנתרים': 'מכוון פסנתר',
  'צבעים לעבודות חיצוניות': 'צבע חיצוני',
};

const sing = (n: string | null) => n ? (SINGULAR[n] || n) : null;

// Idle examples (Founder 2026-09-27): straight "<profession> ב<city>" — no "אני צריך" — and each one drives the home experts
// list below while it shows (demo channel, never persisted as the visitor's intent).
// SUPPLY-GATED from the DB (lib/homeSamplesServer getHomeSamples → layout → SiteShell → here); app/page.tsx renders sample 0's experts.

const WIDGETS: { key: keyof Fields; label: string; icon: string; color: string; border: string }[] = [
  { key: 'cat',    label: 'בעל מקצוע', icon: '🔧', color: '#1e40af', border: '#93c5fd' },
  { key: 'city',   label: 'מיקום',     icon: '📍', color: '#166534', border: '#86efac' },
  { key: 'need',   label: 'הבעיה',     icon: '💬', color: '#5b21b6', border: '#c4b5fd' },
  { key: 'timing', label: 'למתי',       icon: '🗓️', color: '#155e75', border: '#67e8f9' },
];

type Parsed = { forText: string; slug: string | null; name: string | null; city: string | null; cityFuzzy?: boolean; cleanNeed: string | null; priceHint: string | null; timingHint: string | null; wordCount: number };

export default function SmartIntakeBar({ samples = [], inline = false }: { samples?: HomeSample[]; inline?: boolean } = {}) {
  const SAMPLE_SETS = samples;
  const [text, setText] = useState('');
  const [categories, setCategories] = useState<CategoryHint[]>([]);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [sampleIdx, setSampleIdx] = useState(0);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const parseSeq = useRef(0);
  const inflight = useRef<{ text: string; p: Promise<Parsed> } | null>(null);
  const categoriesRef = useRef<CategoryHint[]>([]);

  useEffect(() => {
    fetch('/api/categories').then(r => r.json()).then(d => { setCategories(d.categories || []); categoriesRef.current = d.categories || []; }).catch(() => {});
  }, []);

  // ONE parser for the grid, the chips AND submit (bughunter 2026-09-27: submit used to re-parse locally and land elsewhere).
  // Server first (lib/searchResolve parseLive — synonyms, typos, prefixes, abbreviations, typo'd trailing city); the local
  // engine only adds timing/price and the need (the need only for real sentences, never a leftover city fragment).
  function runParse(t: string): Promise<Parsed> {
    if (inflight.current?.text === t) return inflight.current.p;
    const p = (async () => {
      const r = classifyIntake(t, categoriesRef.current, DEFAULT_SMART_INTAKE_RULES);
      const wordCount = t.split(/\s+/).filter(Boolean).length;
      let slug = r.categorySlug, name = r.categoryName, city = r.city; let cityFuzzy = false;
      try {
        const s = await fetch(`/api/search/parse?q=${encodeURIComponent(t)}`).then(x => x.json());
        slug = s.category?.slug || null; name = s.category?.name || null; city = s.city || null; cityFuzzy = !!s.cityFuzzy;
      } catch {}
      return { forText: t, slug, name, city, cityFuzzy, cleanNeed: wordCount >= 4 ? r.cleanNeed : null, priceHint: r.priceHint, timingHint: r.timingHint, wordCount };
    })();
    inflight.current = { text: t, p };
    return p;
  }

  // Typing: the grid follows from the FIRST word. While a parse is pending the grid keeps what it shows (no generic flash);
  // nothing is written to the site-wide smartIntakeCtx until the visitor actually searches.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const t = text.trim();
    if (!t) { setParsed(null); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++parseSeq.current;
      const r = await runParse(t);
      if (seq !== parseSeq.current) return; // a newer keystroke already won
      setParsed(r);
      // the city is saved the moment it is understood (Founder 2026-09-28) — the questionnaire, the menu and the experts
      // lists read it live; the category is NOT saved until the visitor searches (an abandoned query must not stick)
      // …but only an EXACT city — a typo-guess ("בקריית" half-typed → קריות) is shown, never saved while typing (bughunter)
      if (r.city && !r.cityFuzzy) { const c = findCity(r.city); if (c) setKnownCity(c.name, { lat: c.lat, lng: c.lng }); }
      // a half-typed word that matches nothing yet ("אינסט…" at human speed) must not flash the generic grid — only a pause
      // of ~0.8s makes "nothing matched" final (bughunter re-verify 2026-09-27 #4)
      if (!r.slug && !r.city) { await new Promise(res => setTimeout(res, 800)); if (seq !== parseSeq.current) return; }
      setSmartIntakeDemo({ categorySlug: r.slug, categoryName: r.name, city: r.city, typed: true });
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [text]);

  // Idle: the example rotates in the bar only (30/09/26 — the experts list below stays on its server-rendered set until the
  // visitor types; a list that swapped by itself every 5s read as "the page refreshed and loaded other experts"). Rotation pauses while the input is focused (the visitor is about
  // to type), while they read down the page, or when the tab is hidden.
  useEffect(() => {
    if (text || focused || SAMPLE_SETS.length < 2) return;
    const t = setInterval(() => { if (window.scrollY > 160 || document.hidden) return; setSampleIdx(i => (i + 1) % SAMPLE_SETS.length); }, 5000);
    return () => clearInterval(t);
  }, [text, focused]);
  useEffect(() => {
    const s = SAMPLE_SETS[sampleIdx];
    if (text || !s) return;
    parseSeq.current++; // a late typed parse must not overwrite the example
    setSmartIntakeDemo({ categorySlug: s.slug, categoryName: s.name, city: s.city });
  }, [text, sampleIdx, SAMPLE_SETS.length]); // length, not the array: a fresh [] per render must not re-fire the demo
  useEffect(() => () => setSmartIntakeDemo(null), []);

  function clear() {
    setText(''); setParsed(null);
    inputRef.current?.focus();
  }

  async function doSearch() {
    const t = text.trim();
    if (!t) return;
    const r = parsed?.forText === t ? parsed : await runParse(t);
    // the visitor searched → NOW it becomes the site-wide context, merged (a city answered elsewhere survives a query without one)
    try {
      const prev: SmartIntakeContext = JSON.parse(localStorage.getItem(SMART_INTAKE_KEY) || 'null') || ({} as SmartIntakeContext);
      const sameCity = !r.city || r.city === prev.city;
      localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify({
        ...prev,
        categorySlug: r.slug ?? prev.categorySlug ?? null, categoryName: r.name ?? prev.categoryName ?? null,
        cleanNeed: r.cleanNeed, priceHint: r.priceHint, timingHint: r.timingHint,
        ...(sameCity ? {} : { city: r.city, cityLat: null, cityLng: null, cityAnsweredAt: Date.now() }),
      })); notifySmartIntake();
    } catch {}
    if (r.slug) window.location.href = r.city ? `/${encodeURIComponent(r.slug)}/${encodeURIComponent(citySlug(r.city))}` : `/${encodeURIComponent(r.slug)}`;
    else window.location.href = `/search?q=${encodeURIComponent(t)}`; // the full resolver (AI fallback, "didn't find" page) — never a bare index
  }

  const sample = SAMPLE_SETS[sampleIdx];
  const isTyping = !!text;
  const display: Fields = parsed
    ? { cat: sing(parsed.name), city: parsed.city, need: parsed.cleanNeed ? (parsed.cleanNeed.length > 26 ? parsed.cleanNeed.slice(0, 26) + '…' : parsed.cleanNeed) : null, timing: parsed.timingHint }
    : { cat: null, city: null, need: null, timing: null };

  return (
    <div dir="rtl" style={inline ? { flex: 1, minWidth: 0, position: 'relative' } : { background: 'var(--sk-band-grad, linear-gradient(175deg, #0a2e6e 0%, #0d47a1 70%, #1565c0 100%))', borderBottom: '2px solid rgba(255,255,255,.06)' }}>
      <div className={inline ? undefined : 'max-w-4xl mx-auto'} style={inline ? undefined : { padding: '10px 16px' }}>

        {/* ── ONE ROW (Founder 2026-09-27): input + smart-search button in a single pill. What the engine understood shows as a
             thin chip line ONLY while typing. ── */}
        <form onSubmit={e => { e.preventDefault(); doSearch(); }} role="search" style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 999, boxShadow: '0 4px 20px rgba(0,0,0,.22)', padding: 5, paddingInlineStart: 16 }}>
          <input
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={e => { if (e.key === 'Escape' && text) { e.preventDefault(); clear(); } }}
            placeholder={sample?.query || 'איזה בעל מקצוע צריך ובאיזו עיר?'}
            aria-label="חיפוש חכם — מה צריך ואיפה"
            enterKeyHint="search"
            style={{ flex: 1, minWidth: 0, height: 44, background: 'transparent', color: '#1a1a2e', border: 'none', outline: 'none', fontSize: 16, fontWeight: 500, fontFamily: 'inherit', textOverflow: 'ellipsis' }}
          />
          {text && <button type="button" onClick={clear} aria-label="ניקוי" style={{ width: 44, height: 44, background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', flexShrink: 0 }}>×</button>}
          <button type="submit" disabled={!text.trim()} aria-label="חיפוש חכם"
            style={{ height: 44, flexShrink: 0, padding: '0 16px', borderRadius: 999, border: 'none', background: 'var(--wa, #12864A)', color: '#fff', fontSize: 14, fontWeight: 800, cursor: text.trim() ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <span aria-hidden>✨</span>חיפוש חכם
          </button>
        </form>
        {isTyping && (
          <div aria-live="polite" style={inline ? { display: 'flex', gap: 6, flexWrap: 'wrap', position: 'absolute', insetInline: 0, top: 'calc(100% + 14px)', zIndex: 45 } : { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {WIDGETS.filter(w => display[w.key]).map(w => (
              <span key={w.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 28, padding: '0 10px', borderRadius: 999, background: '#fff', border: `1.5px solid ${w.border}`, color: w.color, fontSize: 12.5, fontWeight: 800 }}>
                <span aria-hidden>{w.icon}</span>{display[w.key]}
              </span>))}
          </div>
        )}
      </div>
      <style>{`[role=search] input::placeholder { color: rgba(26,26,46,.42) !important; }`}</style>
    </div>
  );
}
